import { useTranslation } from 'react-i18next'
import { mdiClipboardCheckOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { cn } from '../../lib/utils'
import {
  INSPECTION_STATUS,
  InspectionList,
  formatInspectionDate,
  headlineInspection,
} from '../compliance/InspectionList'
import type { AssetInspection } from '../../hooks/useComplianceApi'

/**
 * Vérifications cell of the trade equipment table, beside Maintenance and
 * Garantie: the periodic inspections (rules of class `periodic_inspection`) of
 * this equipment. The button takes the tone of the most actionable inspection
 * and prints its last date and next due date; a click opens the list with the
 * reports. No inspection applies → a muted, inert icon, like a missing contract.
 */
export function InspectionsCell({
  org,
  items,
  testid,
}: {
  org: string
  items: AssetInspection[]
  testid: string
}) {
  const { t, i18n } = useTranslation('assets')
  const headline = headlineInspection(items)

  if (!headline) {
    const none = t('equipment_tab.row.no_inspection')
    return (
      <span
        className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground/50"
        title={none}
        aria-label={none}
        data-testid={testid}
        data-active="false"
      >
        <AppIcon path={mdiClipboardCheckOutline} size="xs" />
      </span>
    )
  }

  const tone = INSPECTION_STATUS[headline.status] ?? INSPECTION_STATUS.UNKNOWN
  const title = t('equipment_tab.row.inspections_title', { count: items.length })

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex min-w-0 max-w-full items-center gap-1.5 rounded-md border px-1.5 py-0.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            tone.cell,
          )}
          title={title}
          aria-label={title}
          data-testid={testid}
          data-active="true"
          data-status={headline.status}
        >
          <AppIcon path={tone.icon} size="xs" className="shrink-0" />
          <span className="min-w-0 leading-tight">
            <span className="block truncate text-[11px] text-foreground" data-testid={`${testid}-last`}>
              {formatInspectionDate(headline.last_inspection_date, i18n.language)}
            </span>
            <span className="block truncate text-[10px]" data-testid={`${testid}-next`}>
              {t('equipment_tab.row.inspection_due', {
                date: formatInspectionDate(headline.next_due_date, i18n.language),
              })}
            </span>
          </span>
          {items.length > 1 && (
            <span className="shrink-0 text-[10px] font-medium tabular-nums">×{items.length}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid={`${testid}-popover`}>
        <div className="mb-2 text-xs font-medium text-muted-foreground">{title}</div>
        <InspectionList org={org} items={items} testid={`${testid}-list`} />
      </PopoverContent>
    </Popover>
  )
}
